import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getLibrary, getWorkspace, listWorkspaceThreads, listWorkspaces } from "../api/rag-api.js";
import { mapLibrary, mapWorkspace } from "../api/mappers.js";
import { canCreateKmWorkspace } from "../lib/km-org-permissions.js";
import { useRagNavRefreshKey } from "./RagNavRefreshContext.js";
import { useRagAuthReady } from "./useRagAuthReady.js";
import {
  buildKmRootNav,
  buildLibraryNav,
  buildWorkspaceChatNav,
  buildWorkspaceNav,
  buildWorkspaceNavSections,
  mergeWorkspaceSidebar,
  type RagNavConfig,
  type RagNavWorkspaceInput,
} from "../rag-nav.js";

function toWorkspaceNavInput(workspace: ReturnType<typeof mapWorkspace>): RagNavWorkspaceInput {
  return {
    id: workspace.id,
    name: workspace.name,
    description: workspace.description,
    kind: workspace.kind,
    ownerId: workspace.ownerId,
    libraryCount: workspace.libraryCount,
    memberCount: workspace.memberCount,
  };
}

export function useRagNavConfig(): RagNavConfig | null {
  const { pathname } = useLocation();
  const refreshKey = useRagNavRefreshKey();
  const { ready } = useRagAuthReady();
  const { user, hasPermission } = useAuth();
  const [config, setConfig] = useState<RagNavConfig | null>(null);

  useEffect(() => {
    if (!ready) {
      return;
    }

    if (!pathname.startsWith("/rag")) {
      setConfig(null);
      return;
    }

    const workspaceMatch = pathname.match(/^\/rag\/workspaces\/([^/]+)/);
    const workspaceId = workspaceMatch?.[1];
    const libraryMatch = pathname.match(/^\/rag\/workspaces\/[^/]+\/libraries\/([^/]+)/);
    const workspaceChatMatch = pathname.match(/^\/rag\/workspaces\/[^/]+\/chat(?:\/|$)/);
    const isKmRoot = pathname === "/rag" || pathname === "/rag/";
    let cancelled = false;

    async function load() {
      const showNewWorkspace = canCreateKmWorkspace(hasPermission);

      try {
        const { workspaces: workspaceData } = await listWorkspaces();
        if (cancelled) return;

        const mappedWorkspaces = workspaceData.map(mapWorkspace);
        const workspaceInputs = mappedWorkspaces.map(toWorkspaceNavInput);
        const workspaceSections = buildWorkspaceNavSections(workspaceInputs, user?.id);

        if (isKmRoot) {
          setConfig(buildKmRootNav(workspaceSections, showNewWorkspace));
          return;
        }

        if (!workspaceId) {
          setConfig(buildKmRootNav(workspaceSections, showNewWorkspace));
          return;
        }

        if (libraryMatch) {
          const libraryId = libraryMatch[1];
          const { library } = await getLibrary(libraryId);
          if (cancelled) return;

          const mappedLibrary = mapLibrary(library);
          setConfig(buildLibraryNav(workspaceId, libraryId, mappedLibrary.name));
          return;
        }

        const { workspace } = await getWorkspace(workspaceId);
        if (cancelled) return;

        if (workspaceChatMatch) {
          const { threads } = await listWorkspaceThreads(workspaceId);
          if (cancelled) return;

          const recentChats = threads.map((thread) => ({
            id: thread.id,
            title: thread.title,
          }));
          setConfig(
            mergeWorkspaceSidebar(
              buildWorkspaceChatNav(workspaceId, mapWorkspace(workspace).name, recentChats),
              workspaceSections,
              showNewWorkspace,
            ),
          );
          return;
        }

        setConfig(
          mergeWorkspaceSidebar(
            buildWorkspaceNav(workspaceId, mapWorkspace(workspace).name),
            workspaceSections,
            showNewWorkspace,
          ),
        );
      } catch {
        if (cancelled) return;

        if (isKmRoot || !workspaceId) {
          setConfig(buildKmRootNav([], showNewWorkspace));
          return;
        }

        if (libraryMatch) {
          const libraryId = libraryMatch[1];
          setConfig(buildLibraryNav(workspaceId, libraryId, "Library"));
          return;
        }

        if (workspaceChatMatch) {
          setConfig(
            mergeWorkspaceSidebar(buildWorkspaceChatNav(workspaceId, "Workspace"), [], showNewWorkspace),
          );
          return;
        }

        setConfig(
          mergeWorkspaceSidebar(buildWorkspaceNav(workspaceId, "Workspace"), [], showNewWorkspace),
        );
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [pathname, refreshKey, ready, user?.id, hasPermission]);

  return config;
}

export function useRagWorkspaces() {
  const { ready } = useRagAuthReady();
  const [workspaces, setWorkspaces] = useState<ReturnType<typeof mapWorkspace>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { workspaces: data } = await listWorkspaces();
      setWorkspaces(data.map(mapWorkspace));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready) {
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { workspaces: data } = await listWorkspaces();
        if (cancelled) return;
        setWorkspaces(data.map(mapWorkspace));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load workspaces");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  return { workspaces, loading, error, reload };
}
